import {
  type UseMutationOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  PostV1OrganizationsByOrganizationIdAssignmentsByAssignmentIdGenerateData,
  PostV1OrganizationsByOrganizationIdAssignmentsByAssignmentIdGenerateError,
  PostV1OrganizationsByOrganizationIdAssignmentsByAssignmentIdGenerateResponse,
} from "../../../client";
import { postV1OrganizationsByOrganizationIdAssignmentsByAssignmentIdGenerateMutation } from "../../../client/@tanstack/react-query.gen";
import { getAssignmentsQueryKey } from "./get-assignments";
import type { Options } from "@hey-api/client-fetch";

export const useGenerateAssignmentBankInfo = (
  options: UseMutationOptions<
    PostV1OrganizationsByOrganizationIdAssignmentsByAssignmentIdGenerateResponse,
    PostV1OrganizationsByOrganizationIdAssignmentsByAssignmentIdGenerateError,
    Options<PostV1OrganizationsByOrganizationIdAssignmentsByAssignmentIdGenerateData>
  > = {},
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...postV1OrganizationsByOrganizationIdAssignmentsByAssignmentIdGenerateMutation(),
    ...options,

    onSuccess: (data, variables, context) => {
      toast.success("Assignment information generated successfully");
      options.onSuccess?.(data, variables, context);
    },

    onError: (error, variables, context) => {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      toast.error(`Failed to generate assignment: ${errorMessage}`);
      options.onError?.(error, variables, context);
    },

    onSettled: (data, error, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: getAssignmentsQueryKey(variables.path.organizationID),
      });
      options.onSettled?.(data, error, variables, context);
    },
  });
};

export const createGenerateAssignmentBankOptions = (
  organizationId: string,
  assignmentId: string,
  input: { url: string } | { info: string },
): Options<PostV1OrganizationsByOrganizationIdAssignmentsByAssignmentIdGenerateData> => {
  if ("url" in input) {
    return {
      path: { organizationID: organizationId, assignmentID: assignmentId },
      query: { url: input.url },
      body: {},
    };
  }

  return {
    path: { organizationID: organizationId, assignmentID: assignmentId },
    body: { info: input.info },
  };
};
